/*
Esta página muestra el detalle de un cliente.
Autor: Carolina Rojas
Fecha: 18/11/2024
*/

import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import DataTable, { TableColumn } from 'react-data-table-component'
import { ChevronLeft } from 'lucide-react'
import { IconButton } from '@radix-ui/themes'

import Client from '../interfaces/Client' // Adjust the import path as needed
import ClientDisplay, { DocCli } from '../interfaces/ClientDisplay'
import useClientFetcher from '../hooks/useClientFetcher'
import { fechaIsoToDD_MM_YYYY, formatCurrency, transformGender, isExpired, insertAfterFirstToken } from '../utils/utils'
import { validateAndIdentifyRut } from '../utils/rutValidation'

import { ButtonAction } from './Buttons'
import { DisplayValueFormat } from './DisplayValue'

import { AppPath } from '@/enums/AppPath'

//Tabla que muestra los datos relevantes de los archivos asociados a cada cliente
const columns: TableColumn<DocCli>[] = [
  {
    name: 'Número',
    selector: (row) => row.number ?? 0,
    sortable: true,
    width: '100px',
  },
  {
    name: 'Tipo de Documento',
    selector: (row) => row.source,
    sortable: true,
    width: '200px',
  },
  {
    name: 'Monto',
    selector: (row) => formatCurrency(row.price ?? 0),
    sortable: true,
    width: '200px',
  },
  {
    name: 'Fecha de Creación',
    selector: (row) => fechaIsoToDD_MM_YYYY(row.date),
    sortable: true,
    width: '200px',
  },
  {
    name: 'Estado',
    selector: (row) => row.status,
    sortable: true,
    width: '300px',
  },
]

// Función que establece el estado de un archivo
const fileStatus = (file: DocCli): string => {
  if (file.voidedAt) {
    return 'Nula'
  }

  if (file.archivedAt) {
    return 'Archivada'
  }

  if (file.completedAt) {
    return 'Completada'
  }

  if (file.cancelledAt) {
    return 'Cancelado'
  }

  if (file.closedAt) {
    return 'Completado'
  }

  if (file.approvedAt) {
    return 'Aprobada'
  }

  if (file.source == 'Cotización' && isExpired(new Date(file.expiresAt ?? new Date()))) {
    return 'Expirada'
  }

  if (file.source == 'Cotización' && !isExpired(new Date(file.expiresAt ?? new Date()))) {
    return 'Vigente'
  }

  if (file.source == 'Compra') {
    return 'Válida'
  }

  if (file.source == 'Nota de venta') {
    return 'Pendiente'
  }

  if (file.source == 'Negocio') {
    return 'En curso'
  }

  return 'Activa'
}

interface FileWithStatus extends DocCli {
  status: string
}
const ClientDetail: React.FC = () => {
  const { errorClient, loading, fetchClientDocs } = useClientFetcher()
  const [dataClient, setDataClient] = useState<ClientDisplay | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  // Extracting id parameter from the route
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  useEffect(() => {
    if (!id) {
      setLocalError('ID is not defined.')

      return // Exit early if `id` is undefined
    }

    const fetchDataAsync = async () => {
      const client = await fetchClientDocs(id)

      if (client) {
        const adaptedClient = convertToDisplay(client)
        setDataClient(adaptedClient)
      }
    }

    fetchDataAsync()
  }, [id])

  const convertToDisplay = (client: Client): ClientDisplay => {
    const c1 = client.address
    const c2 = (client.streetNumber ?? '').toString()
    const c3 = client.addressComponents ?? ''
    const fullAddress = insertAfterFirstToken(c1, c2, c3)
    const createdAtFormat = fechaIsoToDD_MM_YYYY(client.createdAt)
    const genderFormat = transformGender(client.gender)
    const birthFormat = fechaIsoToDD_MM_YYYY(client.dateBirth)

    const filesWithStatus: FileWithStatus[] =
      (client.files ?? []).map((file) => ({
        ...file,
        status: fileStatus(file), // Asignamos el estado a cada archivo
      })) || []

    return {
      ...client,
      fullAddress,
      createdAtFormat,
      genderFormat,
      birthFormat,
      files: filesWithStatus,
    }
  }

  const handleEditar = () => {
    const mode = true
    navigate(`/${AppPath.Clients}/edit/${dataClient?._id}`, { state: { mode } })
  }

  const onRowClickedHandler = (row: DocCli): void => {
    if (row.source == 'Compra') {
      navigate(`/${AppPath.Purchases}/view/${row._id}`)
    }

    if (row.source == 'Consignación') {
      navigate(`/${AppPath.Consignments}/view/${row._id}`)
    }

    if (row.source == 'Cotización') {
      navigate(`/${AppPath.Estimates}/view/${row._id}`)
    }

    if (row.source == 'Liquidación') {
      navigate(`/${AppPath.Settlements}/view/${row._id}`)
    }

    if (row.source == 'Negocio') {
      navigate(`/${AppPath.Deals}/view/${row._id}`)
    }

    if (row.source == 'Nota de venta') {
      navigate(`/${AppPath.SaleNotes}/view/${row._id}`)
    }

    if (row.source == 'Reserva') {
      navigate(`/${AppPath.Reservations}/view/${row._id}`)
    }
  }

  if (loading) return <div>Loading...</div>

  if (errorClient) return <div>Error: {errorClient.message}</div>

  if (localError) return <div>Error: {localError}</div>

  const getClientType = () => {
    if (dataClient?.type) return dataClient.type;
    if (dataClient?.nin) return validateAndIdentifyRut(dataClient.nin).type;
    return 'Natural Person';
  };
  const isCompany = getClientType() === 'Legal Entity';

  return (
    <div className="mb-4">
      <div className="text-2xl font-bold ml-4 gap-4 flex items-center mb-4">
        <IconButton variant="ghost" color="gray" onClick={() => navigate(-1)} aria-label="Volver atrás">
          <ChevronLeft />
        </IconButton>
        {`${dataClient?.firstname || ''} ${dataClient?.lastname || ''}`.trim()}
        {isCompany && (
          <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-0.5 rounded-full font-medium border border-indigo-200 ml-2">
            Empresa
          </span>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 mb-4 mt-4">
          <DisplayValueFormat label="Email" value={dataClient?.email} />
          <DisplayValueFormat label="Teléfono" value={dataClient?.phone} />
          <DisplayValueFormat label="RUT" value={dataClient?.nin} />
          {!isCompany && dataClient?.birthFormat && <DisplayValueFormat label="Fecha de Nacimiento" value={dataClient.birthFormat} />}
          {!isCompany && <DisplayValueFormat label="Género" value={dataClient?.genderFormat} />}
          <DisplayValueFormat label="Dirección" value={dataClient?.fullAddress} />
          <DisplayValueFormat label="Creado por" value={dataClient?.createdBy_user} />
          <DisplayValueFormat label="Fecha de creación" value={dataClient?.createdAtFormat} />
        </div>

        {dataClient?.files && (
          <div className="mt-4">
            <DataTable
              columns={columns}
              data={dataClient.files}
              highlightOnHover
              pointerOnHover
              pagination
              onRowClicked={onRowClickedHandler}
            />
          </div>
        )}

        <div className="flex justify-start">
          <ButtonAction label="Modificar" handleButtonClick={handleEditar} />
        </div>
      </div>
    </div>
  )
}

export default ClientDetail
